import { useState, useMemo, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/ui/card";
import { Plus } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { Lightbulb, Info, AlertTriangle, CheckCircle, HelpCircle, Star, Zap, Target, Shield, Clock, Users, Settings, FileText } from "lucide-react";
import { TipFormDialog } from "./tipsGuidance/TipsForm";
import { TipDetailView } from "./tipsGuidance/TipsDetails";
import { TipTable } from "./tipsGuidance/TipsTable";
import { useDeleteFaq, useTipsData, useUpdateTipStatus, useSearchTips, useDashboardDetails} from "../hooks"; 
import { useUtilityServices } from "@features/mx/readCycleManagement/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useActiveUtilityServices } from "@shared/hooks/useActiveUtililyServices";
import { useSearchParams } from 'react-router-dom';

// Define PAGE_SIZE constant
const PAGE_SIZE = 10;

interface Tip {
  id: string;
  title: string;
  quickSummary: string;
  description: string;
  utilityService: string;
  icon: string;
  isActive: boolean;
  order: number;
  helpfulCount: number;
}

const availableIcons = [
  { name: "Lightbulb", icon: Lightbulb, value: "lightbulb" },
  { name: "Info", icon: Info, value: "info" },
  { name: "Alert Triangle", icon: AlertTriangle, value: "alert-triangle" },
  { name: "Check Circle", icon: CheckCircle, value: "check-circle" },
  { name: "Help Circle", icon: HelpCircle, value: "help-circle" },
  { name: "Star", icon: Star, value: "star" },
  { name: "Zap", icon: Zap, value: "zap" },
  { name: "Target", icon: Target, value: "target" },
  { name: "Shield", icon: Shield, value: "shield" },
  { name: "Clock", icon: Clock, value: "clock" },
  { name: "Users", icon: Users, value: "users" },
  { name: "Settings", icon: Settings, value: "settings" },
  { name: "File Text", icon: FileText, value: "file-text" }
];

export const TipsSettings = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Internal pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Read filters from URL (excluding page)
  const searchQuery = decodeURIComponent(searchParams.get("query") || "");
  const filterService = searchParams.get("utilityService") || "";

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterService]);

  // Build query parameters for API
  const queryParams = useMemo(() => ({
    remote_utility_id: remoteUtilityId,
    is_pagination_required: true,
    page: currentPage,
    limit: PAGE_SIZE,
    show_inactive: true,
    ...(filterService && { utility_service: filterService }),
  }), [remoteUtilityId, currentPage, filterService]);

  // Search parameters for search API (only when there's a search query)
  const searchApiParams = useMemo(() => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      return {
        remote_utility_id: remoteUtilityId,
        search_data: "",
        search_field: "tips"
      };
    }
    return {
      remote_utility_id: remoteUtilityId,
      search_data: searchQuery,
      search_field: "tips"
    };
  }, [remoteUtilityId, searchQuery]);

  // Dashboard details hook
  const dashboardDetailsParams = useMemo(() => ({
    remote_utility_id: remoteUtilityId,
  }), [remoteUtilityId]);

  // Use search API when there's a search query, otherwise use regular API
  const { data: regularData, refetch: refetchRegular } = useTipsData(queryParams);
  const { data: searchData, refetch: refetchSearch } = useSearchTips(searchApiParams);
  const { refetch: refetchDashboardDetails } = useDashboardDetails(dashboardDetailsParams);

  // Add debugging

  // Determine which data to use
  const data = searchQuery && searchQuery.trim().length > 0 ? searchData : regularData;
  const refetch = searchQuery && searchQuery.trim().length > 0 ? refetchSearch : refetchRegular;

  // Get utility services from API
  const { data: utilities } = useActiveUtilityServices();
  
  const { mutate: updateTipStatus, isPending: isUpdatingStatus } = useUpdateTipStatus();
  const { mutate: deleteTip, isPending: isDeletingTip } = useDeleteFaq();

  const { toast } = useToast();

  // Transform API data to match Tip interface
  const tips = useMemo(() => {
 
    
    // Handle search API response structure
    if (searchQuery && searchQuery.trim().length > 0) {
      const results = data?.results || data?.result || data || [];
    
      
      return results.map((item: any, index: number) => ({
        id: item.code || item.id || `search-${index}`,
        title: item.name || item.title || '',
        quickSummary: item.quick_summary || item.quickSummary || '',
        description: item.description || '',
        utilityService: item.utility_service || item.utilityService || '',
        icon: item.icon || "lightbulb",
        isActive: item.is_active ?? item.isActive ?? true,
        order: index + 1,
        helpfulCount:item.isHelpful || 0
      }));
    }
    
    // Handle regular API response structure
    const results = data?.result || [];
  
    return results.map((item: any, index: number) => ({
      id: item.code || item.id,
      title: item.name || item.title,
      quickSummary: item.quick_summary || item.quickSummary,
      description: item.description,
      utilityService: item.utility_service || item.utilityService,
      icon: item.icon || "lightbulb",
      isActive: item.is_active ?? item.isActive ?? true,
      order: ((currentPage - 1) * PAGE_SIZE) + index + 1,
      helpfulCount:item.isHelpful|| 0
    }));
  }, [data, searchQuery, currentPage]);

  const [editingTip, setEditingTip] = useState<Tip | null>(null);
  const [viewingTip, setViewingTip] = useState<Tip | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Get utility services from API with proper filtering and mapping
  const utilityServices = utilities
    ? utilities
        .filter((utility) => utility.isActive === true)
        .map((utility) => utility.name)
    : [];

  const handleSearch = (searchTerm: string, paramName: "search_data") => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set("query", searchTerm);
    } else {
      params.delete("query");
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setCurrentPage(1);

    setSearchParams(params);
  };

  const handleApplyFilters = (filters: { utilityService?: string }) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("utilityService");
    params.delete("page");

    // Add new filter params
    if (filters.utilityService) {
      params.set("utilityService", filters.utilityService);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setCurrentPage(1);

    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("utilityService");
    params.delete("page");
    params.delete("query");

    setCurrentPage(1);
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", page.toString());
    setSearchParams(params);
    setCurrentPage(page);
  };

  const handleSaveTip = (tipData: Partial<Tip>) => {
    if (editingTip) {
      // TODO: Call API to update tip
      toast({
        title: "Tip Updated",
        description: "The tip has been successfully updated.",
      });
    } else {
      // For new tips, refetch the data to show the newly created tip
      refetch();
      toast({
        title: "Tip Created",
        description: "A new tip has been successfully created.",
      });
    }
    setEditingTip(null);
    setIsDialogOpen(false);
  };

  const handleDeleteTip = (id: string) => {
    const payload = {
      type:"tips",
      remote_utility_id: remoteUtilityId,
      code: id
    };

    deleteTip(payload, {
      onSuccess: () => {
        refetch(); // Refetch data to update the UI
        toast({
          title: "Tip Deleted",
          description: "The tip has been successfully deleted.",
        });
      },
      onError: (error) => {
        console.error('Failed to delete tip:', error);
        toast({
          title: "Error",
          description: "Failed to delete tip. Please try again.",
          variant: "destructive",
        });
      }
    });
  };

  const handleToggleActive = (tip: Tip) => {
    const payload = {
      code: tip.id,
      is_active: !tip.isActive,
      remote_utility_id: remoteUtilityId 
    };

    updateTipStatus(payload, {
      onSuccess: () => {
        refetch(); // Refetch tips data to update the UI
        refetchDashboardDetails(); // Refetch dashboard details after successful tip status update
        toast({
          title: tip.isActive ? "Tip Deactivated" : "Tip Activated",
          description: `The tip has been ${tip.isActive ? "deactivated" : "activated"}.`,
        });
      },
      onError: (error) => {
        console.error('Failed to update tip status:', error);
        toast({
          title: "Error",
          description: "Failed to update tip status. Please try again.",
          variant: "destructive",
        });
      }
    });
  };

  const handleViewTip = (tip: Tip) => {
    setViewingTip(tip);
    setIsViewDialogOpen(true);
  };

  const handleEditTip = (tip: Tip) => {
    setEditingTip(tip);
    setIsDialogOpen(true);
  };

  // Current filter values for the filter panel
  const currentFilters = useMemo(() => ({
    utilityService: filterService,
  }), [filterService]);

  // Pagination object
  const pagination = useMemo(() => {
    if (searchQuery) {
      // For search results, we might not have pagination info, so calculate based on results length
      const totalItems = data?.results?.length || 0;
      return {
        currentPage: 1, // Search results are typically on single page
        totalPages: 1,
        totalItems,
        startItem: 1,
        endItem: totalItems,
        itemsPerPage: totalItems,
      };
    } else {
      // Regular pagination for non-search results
      return data ? {
        currentPage,
        totalPages: Math.ceil(data.count / PAGE_SIZE),
        totalItems: data.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, data.count),
        itemsPerPage: PAGE_SIZE,
      } : undefined;
    }
  }, [data, searchQuery, currentPage]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 page-subheading">
                <Lightbulb className="h-5 w-5" />
                Tips & Guidance Management
              </CardTitle>
              <CardDescription className="page-subtitle">
                Configure helpful tips that appear throughout the application to guide users.
              </CardDescription>
            </div>
            <Button 
              onClick={() => {
                setEditingTip(null);
                setIsDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Tip
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <TipTable
            tips={tips}
            availableIcons={availableIcons}
            onView={handleViewTip}
            onEdit={handleEditTip}
            onDelete={handleDeleteTip}
            onToggleActive={handleToggleActive}
            isUpdatingStatus={isUpdatingStatus}
            searchPlaceholder="Search tips by title, summary, or service..."
            searchParamName="query"
            searchDebounceMs={300}
            onSearch={handleSearch}
            currentFilters={currentFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            utilityServices={utilityServices}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {isDialogOpen && (
        <TipFormDialog
          tip={editingTip}
          availableIcons={availableIcons}
          utilityServices={utilityServices}
          onSave={handleSaveTip}
          onRefetch={refetch}
          onCancel={() => {
            setEditingTip(null);
            setIsDialogOpen(false);
          }}
        />
      )}

      <TipDetailView
        tip={viewingTip}
        isOpen={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        availableIcons={availableIcons}
      />
    </div>
  );
};